import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'query-string';
import history from 'component/core/history';
import { auth } from './auth';

const api = axios.create({
    baseURL: '/api',
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'none' }),
    headers: {
        'Cache-Control': 'no-cache',
        'Content-Type': 'application/json'
    }
});

// 🔹 Добавляем access токен в каждый запрос
api.interceptors.request.use((config) => {
    const token = auth.getAccess();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
    refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
};

api.interceptors.response.use(
    (response: AxiosResponse) => {
        const answer = response?.data;

        if (answer) {
            // вариант 1: API формат {"is_ok": true, "data": {...}}
            if (answer.is_ok && answer.data) {
                return answer.data;
            }

            // вариант 2: API формат {"data": {...}}
            if (answer.data && Object.keys(answer).length === 1) {
                return answer.data;
            }

            // ✅ просто вернуть тело ответа как есть
            return answer;
        }

        // ⚠️ только если вообще нет data
        return undefined;
    },
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            const refreshToken = auth.getRefresh();

            if (!refreshToken) {
                auth.clear();
                history.push('/login');

                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const { data } = await axios.post('/api/token/refresh/', { refresh: refreshToken });

                // Обновляем токены
                auth.setTokens(data.access, data.refresh || refreshToken);

                // Уведомляем всех ожидающих
                onRefreshed(data.access);

                // Повторяем оригинальный запрос
                originalRequest.headers.Authorization = `Bearer ${data.access}`;

                return api(originalRequest);
            } catch (err) {
                auth.clear();
                history.push('/login');

                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export const request = async <T = void>(config: AxiosRequestConfig): Promise<T> => {
    const response = await api.request<T>(config);

    return response as unknown as T;
};
